import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import type { MainContentTab } from '../store/layoutStore';

export const TARGET_TYPE = {
  WEB_APPLICATION: 'WEB_APPLICATION',
  AI_APPLICATION: 'AI_APPLICATION',
  EXTERNAL_NETWORK: 'EXTERNAL_NETWORK',
  INTERNAL_NETWORK: 'INTERNAL_NETWORK',
} as const satisfies Record<TargetTypeType, TargetTypeType>;

export type TargetScope = {
  targetId: string;
  targetType: TargetTypeType;
};

export type TargetTypeDisplay = {
  label: string;
  colorPalette: 'blue' | 'purple' | 'orange' | 'green';
};

export const TARGET_TYPE_DISPLAY: Record<TargetTypeType, TargetTypeDisplay> = {
  [TARGET_TYPE.WEB_APPLICATION]: {
    label: 'Web App',
    colorPalette: 'blue',
  },
  [TARGET_TYPE.AI_APPLICATION]: {
    label: 'AI App',
    colorPalette: 'purple',
  },
  [TARGET_TYPE.EXTERNAL_NETWORK]: {
    label: 'Ext. Network',
    colorPalette: 'orange',
  },
  [TARGET_TYPE.INTERNAL_NETWORK]: {
    label: 'Int. Network',
    colorPalette: 'green',
  },
};

export function getTargetTypeDisplay(
  targetType: TargetTypeType,
): TargetTypeDisplay {
  return TARGET_TYPE_DISPLAY[targetType];
}

export const TARGET_TYPE_ORDER: TargetTypeType[] = [
  TARGET_TYPE.WEB_APPLICATION,
  TARGET_TYPE.AI_APPLICATION,
  TARGET_TYPE.EXTERNAL_NETWORK,
  TARGET_TYPE.INTERNAL_NETWORK,
];

export type TargetCapability =
  | 'domainTree'
  | 'endpoints'
  | 'codeGraph'
  | 'reports'
  | 'executions'
  | 'credentials'
  | 'copilot'
  | 'proxyTools'
  | 'webPages'
  | 'traces'
  | 'testingInstructions'
  | 'signals'
  | 'findings'
  | 'assets'
  | 'agentExecutions'
  | 'networkOverview'
  | 'networkGraph';

const APPLICATION_CAPABILITIES: Record<TargetCapability, boolean> = {
  domainTree: true,
  endpoints: true,
  codeGraph: true,
  reports: true,
  executions: true,
  credentials: true,
  copilot: true,
  proxyTools: true,
  webPages: true,
  traces: true,
  testingInstructions: true,
  signals: true,
  findings: true,
  assets: true,
  agentExecutions: false,
  networkOverview: false,
  networkGraph: false,
};

const NETWORK_CAPABILITIES: Record<TargetCapability, boolean> = {
  domainTree: false,
  endpoints: false,
  codeGraph: false,
  reports: false,
  executions: false,
  credentials: false,
  copilot: true,
  proxyTools: true,
  webPages: false,
  traces: false,
  testingInstructions: false,
  signals: true,
  findings: true,
  assets: true,
  agentExecutions: true,
  networkOverview: true,
  networkGraph: true,
};

export const TARGET_CAPABILITIES: Record<
  TargetTypeType,
  Record<TargetCapability, boolean>
> = {
  [TARGET_TYPE.WEB_APPLICATION]: APPLICATION_CAPABILITIES,
  [TARGET_TYPE.AI_APPLICATION]: APPLICATION_CAPABILITIES,
  [TARGET_TYPE.EXTERNAL_NETWORK]: NETWORK_CAPABILITIES,
  [TARGET_TYPE.INTERNAL_NETWORK]: NETWORK_CAPABILITIES,
};

export function targetSupports({
  targetType,
  capability,
}: {
  targetType: TargetTypeType;
  capability: TargetCapability;
}): boolean {
  return TARGET_CAPABILITIES[targetType][capability];
}

export function isAppTarget(targetType: TargetTypeType): boolean {
  return (
    targetType === TARGET_TYPE.WEB_APPLICATION ||
    targetType === TARGET_TYPE.AI_APPLICATION
  );
}

export function isNetworkAttackTarget(targetType: TargetTypeType): boolean {
  return (
    targetType === TARGET_TYPE.EXTERNAL_NETWORK ||
    targetType === TARGET_TYPE.INTERNAL_NETWORK
  );
}

export function supportsPortalSignalAttack({
  targetType,
  targetId,
  appTargetId,
}: {
  targetType: TargetTypeType | undefined;
  targetId: string | undefined;
  appTargetId: string | undefined;
}): boolean {
  if (!targetType || !targetId) {
    return false;
  }

  if (isAppTarget(targetType)) {
    return Boolean(appTargetId);
  }

  return isNetworkAttackTarget(targetType);
}

export function isTargetType(value: string): value is TargetTypeType {
  return TARGET_TYPE_ORDER.some((targetType) => targetType === value);
}

export const MAIN_CONTENT_TAB_REQUIRED_CAPABILITY: Partial<
  Record<MainContentTab, TargetCapability>
> = {
  'assets-list': 'assets',
  'surface-asset': 'assets',
  'ai-component': 'assets',
  'code-list': 'codeGraph',
  'reports-list': 'reports',
  'executions-list': 'executions',
  'agent-executions-list': 'agentExecutions',
  'network-graph': 'networkGraph',
  endpoint: 'endpoints',
  domain: 'endpoints',
  webpage: 'webPages',
  executions: 'executions',
  'agent-execution': 'agentExecutions',
  'co-pilot-execution': 'copilot',
  settings: 'credentials',
};

export function mainContentTabSupportsTarget({
  tabType,
  targetType,
}: {
  tabType: MainContentTab;
  targetType: TargetTypeType;
}): boolean {
  const capability = MAIN_CONTENT_TAB_REQUIRED_CAPABILITY[tabType];

  if (!capability) {
    return true;
  }

  return targetSupports({ targetType, capability });
}

export function formatTargetScopeKey({
  targetId,
  targetType,
}: TargetScope): string {
  return `${targetType}:${targetId}`;
}

export function parseTargetScopeKey(scopeKey: string): TargetScope | null {
  const separatorIndex = scopeKey.indexOf(':');

  if (separatorIndex <= 0) {
    return null;
  }

  const rawTargetType = scopeKey.slice(0, separatorIndex);
  const targetId = scopeKey.slice(separatorIndex + 1);

  if (!targetId || !isTargetType(rawTargetType)) {
    return null;
  }

  return { targetType: rawTargetType, targetId };
}

type LegacyPersistedProjectState = {
  applicationId?: string;
  selectedApplication?: unknown;
  trackingApplicationId?: string;
  targetId?: string;
  targetType?: TargetTypeType;
  selectedTarget?: unknown;
  trackingTargetId?: string;
  trackingTargetType?: TargetTypeType;
};

export function migrateLegacyProjectPersistedState(
  persisted: LegacyPersistedProjectState,
): Partial<{
  targetId: string;
  targetType: TargetTypeType;
  selectedTarget: unknown;
  trackingTargetId: string;
  trackingTargetType: TargetTypeType;
}> {
  if (persisted.targetId !== undefined) {
    return {
      targetId: persisted.targetId,
      targetType: persisted.targetType ?? TARGET_TYPE.WEB_APPLICATION,
      selectedTarget: persisted.selectedTarget,
      trackingTargetId: persisted.trackingTargetId,
      trackingTargetType: persisted.trackingTargetType,
    };
  }

  const targetId = persisted.applicationId ?? '';
  const targetType: TargetTypeType = TARGET_TYPE.WEB_APPLICATION;

  return {
    targetId,
    targetType,
    selectedTarget: persisted.selectedTarget ?? persisted.selectedApplication,
    trackingTargetId:
      persisted.trackingTargetId ?? persisted.trackingApplicationId,
    trackingTargetType: TARGET_TYPE.WEB_APPLICATION,
  };
}
